import { Injectable, inject } from '@angular/core';
import { Observable, EMPTY, expand, map, reduce, scan } from 'rxjs';
import { GET_ALL_TRANSACCIONES_PAGINATED } from '../graphql/transacciones.queries';
import { GraphService } from 'src/app/shared/lib/api/graph.service';
import { DataTransformHelper } from 'src/app/shared/lib/helpers';
import { BankTransactionApi, type BankTransactionInput } from 'src/app/entities/bank-transaction';

@Injectable({
  providedIn: 'root',
})
export class TransaccionesService {
  private readonly graphQL = inject(GraphService);
  private readonly helper = inject(DataTransformHelper);
  private readonly bankTransactionApi = inject(BankTransactionApi);

  /**
   * @description Obtiene todas las transacciones bancarias paginadas, manejando la paginación automáticamente
   * @returns Observable con la lista completa de transacciones bancarias
   */
  getAllTransacciones(): Observable<any> {
    const limit = 200;

    return this.getAllTransaccionPaginated(limit).pipe(
      expand((response: any) => {
        const page = response?.getAllTransaccionPaginated;
        if (page?.hasMore && page?.nextToken) {
          return this.getAllTransaccionPaginated(limit, page.nextToken);
        }
        return EMPTY;
      }),
      map((response: any) => response?.getAllTransaccionPaginated?.items ?? []),
      reduce((acc: any[], items: any[]) => [...acc, ...items], []),
      map((items: any[]) => ({ getAllTransaccion: items })),
    );
  }

  getAllTransaccionesProgressive(limit = 200): Observable<{
    items: any[];
    loadedPages: number;
    hasMore: boolean;
  }> {
    return this.getAllTransaccionPaginated(limit).pipe(
      expand((response: any) => {
        const page = response?.getAllTransaccionPaginated;
        return page?.hasMore && page?.nextToken
          ? this.getAllTransaccionPaginated(limit, page.nextToken)
          : EMPTY;
      }),
      map((response: any) => response?.getAllTransaccionPaginated),
      scan(
        (state, page: any) => ({
          items: [...state.items, ...(page?.items ?? [])],
          loadedPages: state.loadedPages + 1,
          hasMore: Boolean(page?.hasMore),
        }),
        { items: [] as any[], loadedPages: 0, hasMore: true as boolean },
      ),
    );
  }

  /**
   * @description Obtiene una página de transacciones bancarias
   * @param limit Cantidad de registros por página
   * @param nextToken Token para obtener la siguiente página
   * @returns Observable con la página de transacciones bancarias
   */
  private getAllTransaccionPaginated(
    limit: number = 100,
    nextToken?: string,
  ): Observable<any> {
    return this.graphQL.queryGraphQL<any>({
      query: GET_ALL_TRANSACCIONES_PAGINATED,
      variables: { limit, nextToken },
    });
  }

  // ========== MÉTODOS ADICIONALES MIGRADOS DE FINANCIAL.SERVICE ==========

  addTransaccionesBanco(transacciones: any): Observable<any> {
    return this.bankTransactionApi.createMany(transacciones as BankTransactionInput[]);
  }

  addTransaccionesBancoTC(transacciones: any): Observable<any> {
    return this.graphQL.mutateGraphQL<any>({
      mutation: `mutation addTransaccionesBancoTC($transacciones:[TransactionTCInput!]!){
        addTransaccionesBancoTC(transacciones:$transacciones)
      }`,
      variables: { transacciones },
    });
  }

  getAllTransaccionesBancoTC(): Observable<any> {
    return this.graphQL.queryGraphQL<any>({
      query: `query {
        getAllTransaccionesBancoTC {
          id
          platform
          currency
          orderAmount
          transactionDate
          transactionCode
          cardholderEmail
          clientName
          niubizDetails {
            orderNumber
            cardBrand
            settlementDate
            status
            merchantCode
            authorizationDate
            PAN
            authorizationCode
            issuer
            cardType
          }
          weTravelDetails {
            dateUpdated
            transactionType
            paymentMethod
            netAmount
            amount
            description
            reference
            paymentPurpose
            runningBalance
            transactionId
            fees {
              weTravelFee
              organizerCardFee
              customerCardFee
              transferFee
            }
            participants {
              name
              email
            }
            trip {
              name
              id
              startDate
              endDate
            }
            package
          }
        }
      }`,
      variables: {},
    });
  }

  getAllTransaccionesPI(): Observable<any> {
    return this.graphQL.queryGraphQL<any>({
      query: `query {
        getAllTransaccionesBancoTC {
          id
          estado
          platform
          currency
          orderAmount
          transactionDate
          transactionCode
          cardholderEmail
          clientName
          cobranzaId
          comision
          pi{
            id,
            total,
            totalPrice,
            taxInformation {
              applyIgv
              totalAmount
            },
            totalPagado,
            estado,
            file,
            Codigo,
            dueDate,
          }
        }
      }`,
      variables: {},
    });
  }

  addTransaccionBanco(transaccion: any): Observable<any> {
    return this.graphQL.mutateGraphQL<any>({
      mutation: `mutation addTransaccionesBanco($transaccion:inputTransaccionBanco!){
        addTransaccionesBanco(transaccion:$transaccion){
          id,
          banco,
          cuenta,
          fecha,
          fechaValuta,
          descripcion,
          tipo,
          monto,
          saldo,
          operacion,
          horaOperacion,
          usuario,
          referencia,
          createdAt,
          updatedAt
        }
      }`,
      variables: { transaccion },
    });
  }

  updateTransaccionTC(id: string, transaccionTC: any): Observable<any> {
    return this.graphQL.mutateGraphQL<any>({
      mutation: `mutation updateTransaccionTC($id: String!,$transaccionTC:TransactionTCInput!){
        updateTransaccionTC(id: $id,transaccionTC:$transaccionTC){
          id
        }
      }`,
      variables: { id, transaccionTC },
    });
  }

  getTransactionsIn(projectionExpression: string): Observable<any> {
    const fields = projectionExpression
      ? this.helper.buildGraphQLFields(
          projectionExpression.split(',').map((f) => f.trim()),
        )
      : 'id';

    const query = `query getAllTransaccionIn($projectionExpression: String){
      getAllTransaccionIn(projectionExpression: $projectionExpression){
        ${fields}
      }
    }`;

    return this.graphQL.queryGraphQL<any>({
      query,
      variables: { projectionExpression },
    });
  }

  getCobranza(id: string): Observable<any> {
    return this.graphQL.queryGraphQL<any>({
      query: `query getCobranza($id: String!){
        getCobranza(id: $id){
          id,
          createdAt,
          fechaEfectiva,
          estado,
          moneda,
          listaOI{
            id,
            estado,
            tipoIngreso,
            categoriaIngreso,
            subcategoriaIngreso,
            numberOI,
            monto,
            moneda,
            deudor{
              nombre,
              tipo
            }
            accountStatement{
              id,
              clientId,
              clientName,
              period,
              startDate,
              endDate,
              dueDate,
              createdAt,
            }
          }
        }
      }`,
      variables: { id },
    });
  }

  getTransactionsOut(projectionExpression: string): Observable<any> {
    const fields = projectionExpression
      ? this.helper.buildGraphQLFields(
          projectionExpression.split(',').map((f) => f.trim()),
        )
      : 'id';

    const query = `query getAllTransaccionOut($projectionExpression: String){
      getAllTransaccionOut(projectionExpression: $projectionExpression){
        ${fields}
      }
    }`;

    return this.graphQL.queryGraphQL<any>({
      query,
      variables: { projectionExpression },
    });
  }

  /**
   * @deprecated El método getAllIDTransaccion está obsoleto y se recomienda utilizar getAllTransacciones() en su lugar.
   * @description Obtiene todos los IDs de transacciones bancarias
   * @returns Observable con la lista de IDs de transacciones bancarias
   */
  getAllIDTransaccion(): Observable<any> {
    return this.graphQL.queryGraphQL<any>({
      query: `query{
        getAllIDTransaccion{
          IdOrder,
        }
      }`,
      variables: {},
    });
  }

  getExistingTransactionIds(ids: string[]): Observable<{ getExistingTransactionIds: string[] }> {
    return this.bankTransactionApi.getExistingIds(ids);
  }

  updateTransaccion(id: string, transaccion: any): Observable<any> {
    return this.graphQL.mutateGraphQL<any>({
      mutation: `mutation updateTransaccion($id: String!,$transaccion:inputTransaccionBanco!){
        updateTransaccion(id: $id,transaccion:$transaccion){
          id
        }
      }`,
      variables: { id, transaccion },
    });
  }

  deleteTransaccion(id: any): Observable<any> {
    return this.graphQL.mutateGraphQL<any>({
      mutation: `mutation deleteTransaccion($id:String!){
        deleteTransaccion(id:$id)
      }`,
      variables: { id },
    });
  }

  // deleteAllTransacciones(): Observable<any> {
  //   return this.graphQL.mutateGraphQL<any>({
  //     mutation: `mutation {
  //       deleteAllTransacciones
  //     }`,
  //     variables: {},
  //   });
  // }

  assignCobranzaToTransaccion(transactionId: any, cobranza: any): Observable<any> {
    return this.graphQL.mutateGraphQL<any>({
      mutation: `mutation assignCobranzaToTransaccion($transactionId:String!,$cobranza: inputCobranza!){
        assignCobranzaToTransaccion(transactionId:$transactionId,cobranza: $cobranza){
          id,
          createdAt,
          fechaEfectiva,
          estado,
          moneda,
          listaOI{
            id,
            estado,
            tipoIngreso,
            categoriaIngreso,
            subcategoriaIngreso,
            numberOI,
            monto,
            moneda,
            deudor{
              nombre,
              tipo
            }
            accountStatement{
              id,
              clientId,
              clientName,
              period,
              startDate,
              endDate,
              dueDate,
              createdAt,
            }
          }
        }
      }`,
      variables: { transactionId, cobranza },
    });
  }

  assignCobranzaToTransaccionTarjeta(transactionId: any, cobranza: any): Observable<any> {
    return this.graphQL.mutateGraphQL<any>({
      mutation: `mutation assignCobranzaToTransaccionTarjeta($transactionId:String!,$cobranza: inputCobranza!){
        assignCobranzaToTransaccionTarjeta(transactionId:$transactionId,cobranza: $cobranza){
          id,
          createdAt,
          fechaEfectiva,
          estado,
          moneda,
          listaOI{
            id,
            estado,
            tipoIngreso,
            categoriaIngreso,
            subcategoriaIngreso,
            numberOI,
            monto,
            moneda,
            deudor{
              nombre,
              tipo
            }
            accountStatement{
              id,
              clientId,
              clientName,
              period,
              startDate,
              endDate,
              dueDate,
              createdAt,
            }
          }
        }
      }`,
      variables: { transactionId, cobranza },
    });
  }

  assignPagoToTransaccion(transactionId: any, pago: any): Observable<any> {
    return this.graphQL.mutateGraphQL<any>({
      mutation: `mutation assignPagoToTransaccion($transactionId:String!,$pago: input_egresoModel!){
        assignPagoToTransaccion(transactionId:$transactionId,pago: $pago)
      }`,
      variables: { transactionId, pago },
    });
  }

  assignMovimientoToTransaccion(transactionId: any, movimiento: any): Observable<any> {
    return this.graphQL.mutateGraphQL<any>({
      mutation: `mutation assignMovimientoToTransaccion($transactionId:String!,$movimiento: inputMovimientoIntraday_Dto!){
        assignMovimientoToTransaccion(transactionId:$transactionId,movimiento: $movimiento)
      }`,
      variables: { transactionId, movimiento },
    });
  }
}
